import {
  AgentRebateSettingsMode,
  LicenceDetailsState,
  type LicenceDetails,
  type Rebate,
} from '@urbanx/agx.agencies.client';
import { AgxFormTextField } from '@urbanx/agx.forms';
import { AgxSpacer, AgxTypography, AgxDropdown } from '@urbanx/agx.ui-components2';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import { useEffect, useRef } from 'react';
import { ReadOnlyRebates } from 'components/listing-agreement/ReadOnlyRebates';
import { RebateListEditor } from 'components/listing-agreement/RebateListEditor';
import { getAgencyLicenceStates } from 'components/settings-section/agencyLicenceStates';
import SettingsSection from 'components/settings-section/SettingsSection';
import { TeamMemberForm, type TeamMemberFormInstance } from './teamMemberFormDefinition';
import './AgentRebates.scss';

export const AgentRebates = ({
  form,
  agencyRebates,
  licences,
}: {
  form: TeamMemberFormInstance;
  agencyRebates: Rebate[];
  licences?: readonly Pick<LicenceDetails, 'state'>[] | null;
}) => {
  const agentRebateDraft = useRef<Rebate[] | undefined>(undefined);
  useEffect(() => {
    if (
      form.phase === 'succeeded' &&
      form.baseline.rebateSettings.mode === AgentRebateSettingsMode.AgencyDefaults
    ) {
      agentRebateDraft.current = undefined;
    }
  }, [form.phase, form.baseline]);

  const states = getAgencyLicenceStates(licences);
  const showApplicability = states.length > 1;
  const rebateStates = [LicenceDetailsState.QLD, LicenceDetailsState.NSW].filter((state) =>
    states.includes(state)
  );

  return (
    <SettingsSection
      id="agent-rebates"
      title="Rebates, Commissions and Benefits"
      description="Included in every listing agreement this agent sends."
      applicability={showApplicability ? rebateStates.join(' & ') : undefined}
      ariaLabel={showApplicability ? `Rebates (${rebateStates.join(' and ')})` : 'Rebates'}
    >
      <AgxSpacer gap="spacing-2xl" fill className="agx-agent-rebates-content">
        <TeamMemberForm.CustomField name="rebateSettings.mode">
          {(field) => (
            <AgxSpacer className="agx-agent-rebates-set">
              <AgxDropdown
                id="agent-rebate-set"
                label="Rebate set"
                hideOptionalText
                selected={field.value}
                disabled={field.disabled}
                options={[
                  { value: AgentRebateSettingsMode.AgencyDefaults, label: 'Agency defaults' },
                  { value: AgentRebateSettingsMode.AgentSpecific, label: 'Agent specific' },
                ]}
                onChange={(value) => {
                  if (value === field.value) return;
                  if (value === AgentRebateSettingsMode.AgencyDefaults) {
                    agentRebateDraft.current = form.values.rebateSettings.rebates;
                    field.onChange(value);
                    form.form.setFieldValue('rebateSettings.rebates', []);
                  } else if (value === AgentRebateSettingsMode.AgentSpecific) {
                    field.onChange(value);
                    form.form.setFieldValue(
                      'rebateSettings.rebates',
                      agentRebateDraft.current ?? agencyRebates.map((rebate) => ({ ...rebate }))
                    );
                  }
                }}
              />
            </AgxSpacer>
          )}
        </TeamMemberForm.CustomField>
        {form.values.rebateSettings.mode === AgentRebateSettingsMode.AgencyDefaults ? (
          <ReadOnlyRebates rebates={agencyRebates} />
        ) : (
          <TeamMemberForm.CustomField name="rebateSettings.rebates">
            {(field) => (
              <RebateListEditor
                emptyLabel="No rebates"
                value={field.value}
                disabled={field.disabled}
                errorText={field.errorText}
                onChange={field.onChange}
                renderField={(index, { name, label, placeholder }) => (
                  <TeamMemberForm.Field
                    key={name}
                    name={`rebateSettings.rebates[${index}].${name}`}
                    Input={AgxFormTextField}
                    type="text"
                    label={label}
                    placeholder={placeholder}
                    stretch
                  />
                )}
              />
            )}
          </TeamMemberForm.CustomField>
        )}
        <AgxTypography type="body_ui" size="small" color={tokens.text.secondary}>
          {form.values.rebateSettings.mode === AgentRebateSettingsMode.AgencyDefaults
            ? 'Agency defaults are read-only. Choose Agent specific to edit the agent’s custom list, or start from agency defaults if no custom list exists.'
            : 'These rebates replace the agency list. An empty list means no rebates for this agent.'}
          {form.baseline.rebateSettings.mode === AgentRebateSettingsMode.AgentSpecific &&
            form.values.rebateSettings.mode === AgentRebateSettingsMode.AgencyDefaults &&
            ' Saving agency defaults will discard this agent’s custom list.'}
        </AgxTypography>
      </AgxSpacer>
    </SettingsSection>
  );
};
